import { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {
  DiscreteAccess,
  getInfo,
  normalizeDiscreteAccess,
  selectUsers,
  setUsers, setStaffSeatCount,
  setCustomers,
  setCRMSettings,
  setSettingsParts,
  setBilling,
  User,
} from './redux';
import { selectOrganizationData, setOrganizationData } from 'pages/redux';
import { toast } from 'utils/toast';
import {
  selectPermission, selectStaffId, selectUsername, setDiscreteAccess, setPermission, setStaffId,
} from 'redux/global';
import { selectUserEmail } from 'pages/app/redux';
import { isSystemBotStaffRow } from 'pages/Records/systemBot';

export const useUpdateSettings = () => {
  const dispatch = useAppDispatch();

  const updateLocalSettings = (part: Record<string, any>) => {
    // Lazy require — this file is imported while the store is still initializing
    // (metrics/redux → Settings/hooks → store).
    // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
    const { store: appStore } = require('redux/store') as typeof import('redux/store');
    const current = (appStore.getState() as any).settings?.parts || {};
    const patch = part?.userPatch;
    if (patch && typeof patch === 'object') {
      const email = String(patch.email || '').toLowerCase();
      const users = ((current.users || []) as User[]).map((user) => (
        String(user.email || '').toLowerCase() === email
          ? { ...user, ...patch }
          : user
      ));
      const { userPatch: _drop, ...rest } = part;
      dispatch(setSettingsParts({ ...current, ...rest, users }));
    } else {
      dispatch(setSettingsParts({ ...current, ...part }));
    }
  };

  return { updateLocalSettings };
};

/** Staff/users for Settings and shared callers — room/rate helpers removed with hospitality. */
export const useSettings = () => {
  const users = useAppSelector(selectUsers);
  return { users };
};

export const useFetchSettings = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadedOrganizationId, setLoadedOrganizationId] = useState<string | null>(null);
  const organizationData = useAppSelector(selectOrganizationData);
  const users = useAppSelector(selectUsers);
  const username = useAppSelector(selectUsername);
  const staffId = useAppSelector(selectStaffId);
  const userEmail = useAppSelector(selectUserEmail);
  const permission = useAppSelector(selectPermission);
  const dispatch = useAppDispatch();
  const organizationId = organizationData?.id != null ? String(organizationData.id) : null;
  const organizationIdRef = useRef(organizationId);
  organizationIdRef.current = organizationId;

  useEffect(() => {
    if (!organizationId) {
      setLoadedOrganizationId(null);
      return undefined;
    }
    if (loadedOrganizationId === organizationId && users !== null) return undefined;

    const requestedOrganizationId = organizationId;
    const f = async () => {
      setIsLoading(true);
      const res = await dispatch(getInfo());
      // Ignore if user switched organizations while this request was in flight.
      if (requestedOrganizationId !== organizationIdRef.current) return;
      setIsLoading(false);
      if (res.status === 'success') {
        dispatch(setUsers((res.data?.users ?? []).filter((user: User) => !isSystemBotStaffRow(user))));
        const nextSeatCount = Number(res.data?.staffSeatCount);
        if (Number.isFinite(nextSeatCount)) {
          dispatch(setStaffSeatCount(nextSeatCount));
          if (organizationData) {
            dispatch(setOrganizationData({
              ...organizationData,
              staffSeatCount: nextSeatCount,
            }));
          }
        }
        dispatch(setBilling(res.data.billing));
        dispatch(setCRMSettings(res.data.crmSettings));
        dispatch(setCustomers(Array.isArray(res.data.customers) ? res.data.customers : []));
        setLoadedOrganizationId(requestedOrganizationId);
        return;
      }
      toast(res.data, { type: 'error' });
    };

    f();
    return undefined;
  }, [organizationId, loadedOrganizationId, users, dispatch]);

  // Keep session permission / discrete access / staffId aligned with staff record.
  useEffect(() => {
    if (!users?.length || (!username && !userEmail)) return;
    const me = users.find((u) => (
      (userEmail && u.email?.toLowerCase() === userEmail.toLowerCase())
      || (username && u.username?.toLowerCase() === username.toLowerCase())
    ));
    if (!me || me.permission == null || me.permission === '') return;
    const staffPerm = Number(me.permission);
    if (!Number.isNaN(staffPerm) && staffPerm !== permission) {
      dispatch(setPermission(staffPerm));
    }
    if (me.id != null && String(me.id) !== '' && String(me.id) !== String(staffId || '')) {
      dispatch(setStaffId(String(me.id)));
    }
    const nextAccess = normalizeDiscreteAccess(staffPerm, me.discreteAccess as DiscreteAccess);
    const current = JSON.parse(localStorage.getItem('discreteAccess') || '{}');
    if (
      Boolean(current.inventoryEdit) !== Boolean(nextAccess.inventoryEdit)
      || Boolean(current.departmentsEdit) !== Boolean(nextAccess.departmentsEdit)
      || Boolean(current.crmEdit) !== Boolean(nextAccess.crmEdit)
    ) {
      dispatch(setDiscreteAccess(nextAccess));
    }
  }, [users, username, userEmail, permission, staffId, dispatch]);

  return { isLoading };
};
